import { useMemo, useState } from "react";
import { matchStore } from "../matching/matchStore";
import type { ReviewFilter } from "../review/queueStore";
import { reviewQueueStore } from "../review/queueStore";
import { ReviewAuthGate } from "./ReviewAuthGate";
import { ReviewDetail } from "./ReviewDetail";
import { ReviewList } from "./ReviewList";
import { useReviewApplication, useReviewQueue } from "./useReviewQueue";

function parseHash(): { filter: ReviewFilter; caseId: string | null } {
  // #/review | #/review/pending | #/review/case/<id>
  const raw = window.location.hash.replace(/^#\/?/, "");
  const parts = raw.split("/").filter(Boolean);
  // ["review", ...]
  if (parts[0] !== "review") {
    return { filter: "pending_review", caseId: null };
  }
  if (parts[1] === "case" && parts[2]) {
    return { filter: "pending_review", caseId: parts[2] };
  }
  const f = parts[1] as ReviewFilter | undefined;
  if (f === "approved" || f === "rejected" || f === "all" || f === "pending_review" || f === "needs_resubmit") {
    return { filter: f, caseId: null };
  }
  return { filter: "pending_review", caseId: null };
}

function setHash(filter: ReviewFilter, caseId: string | null) {
  if (caseId) {
    window.location.hash = `#/review/case/${caseId}`;
  } else if (filter === "pending_review") {
    window.location.hash = "#/review";
  } else {
    window.location.hash = `#/review/${filter}`;
  }
}

/** Public entry — gate mounts first; queue hooks only run after unlock. */
export function ReviewApp() {
  return (
    <ReviewAuthGate>
      <ReviewAppInner />
    </ReviewAuthGate>
  );
}

function ReviewAppInner() {
  const initial = useMemo(() => parseHash(), []);
  const [filter, setFilter] = useState<ReviewFilter>(initial.filter);
  const [selectedId, setSelectedId] = useState<string | null>(initial.caseId);
  const { items, refresh, source, serverApps } = useReviewQueue(filter);
  const { app } = useReviewApplication(selectedId, serverApps);
  const [banner, setBanner] = useState<string | null>(null);

  const onFilter = (f: ReviewFilter) => {
    setFilter(f);
    setSelectedId(null);
    setHash(f, null);
  };

  const onSelect = (id: string) => {
    setSelectedId(id);
    setHash(filter, id);
  };

  const onBack = () => {
    setSelectedId(null);
    setHash(filter, null);
  };

  const seed = () => {
    const n = reviewQueueStore.seedDemoIfEmpty();
    const poolAdded = matchStore.seedDemoPool();
    setBanner(
      n > 0 || poolAdded > 0
        ? `Loaded ${n} demo review case(s) · seeded ${poolAdded} demo profile(s) into matching pool.`
        : "Queue already has cases and matching pool already has demo profiles."
    );
    void refresh();
  };

  const clear = () => {
    if (!window.confirm("Clear all review queue data on this device?")) return;
    reviewQueueStore.clearAll();
    setSelectedId(null);
    setBanner("Queue cleared.");
    void refresh();
  };

  const [resetHandle, setResetHandle] = useState("");
  const [resetPass, setResetPass] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const resetPassphrase = async () => {
    setResetBusy(true);
    setBanner(null);
    try {
      const { apiJson } = await import("../api/client");
      const res = await apiJson<{
        recoveryCode?: string;
        recoveryHint?: string;
        error?: string;
      }>("/api/moderation/reset-passphrase", "POST", {
        handle: resetHandle,
        newPassphrase: resetPass,
      });
      if (!res.ok) {
        setBanner(res.error || "Reset failed.");
        return;
      }
      const code = res.data.recoveryCode || "";
      setBanner(
        `Passphrase reset for ${resetHandle}. New recovery code (show once): ${code}. ${
          res.data.recoveryHint || ""
        }`
      );
      setResetPass("");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div className="rc-shell">
      <div className="rc-frame">
        <div className="rc-toolbar">
          <a className="rc-toolbar__link" href="#/">
            ← Onboarding
          </a>
          <div className="rc-toolbar__actions">
            <span className="rc-toolbar__btn rc-toolbar__btn--ghost" aria-live="polite">
              {source === "server" ? "Live queue" : "This device"}
            </span>
            <button type="button" className="rc-toolbar__btn" onClick={seed}>
              Load demo cases
            </button>
            <button type="button" className="rc-toolbar__btn rc-toolbar__btn--ghost" onClick={clear}>
              Clear queue
            </button>
          </div>
        </div>

        {source === "server" ? (
          <div className="rc-banner" style={{ flexWrap: "wrap", gap: 8 }}>
            <strong style={{ width: "100%" }}>Support — reset passphrase</strong>
            <input
              className="field__input"
              style={{ flex: 1, minWidth: 140 }}
              placeholder="Member handle"
              value={resetHandle}
              onChange={(e) => setResetHandle(e.target.value)}
            />
            <input
              className="field__input"
              style={{ flex: 1, minWidth: 140 }}
              type="password"
              placeholder="New passphrase (8+)"
              value={resetPass}
              onChange={(e) => setResetPass(e.target.value)}
            />
            <button
              type="button"
              className="rc-toolbar__btn"
              disabled={resetBusy || !resetHandle.trim() || resetPass.length < 8}
              onClick={() => void resetPassphrase()}
            >
              {resetBusy ? "…" : "Reset"}
            </button>
          </div>
        ) : null}

        {banner ? (
          <div className="rc-banner" role="status">
            {banner}
            <button type="button" className="rc-banner__close" onClick={() => setBanner(null)}>
              ×
            </button>
          </div>
        ) : null}

        {selectedId && app ? (
          <ReviewDetail
            app={app}
            serverMode={source === "server"}
            onBack={onBack}
            onChanged={() => {
              void refresh();
            }}
          />
        ) : selectedId && !app ? (
          <div className="rc-empty">
            <p>Case not found.</p>
            <button type="button" className="rc-toolbar__btn" onClick={onBack}>
              Back to queue
            </button>
          </div>
        ) : (
          <ReviewList
            items={items}
            filter={filter}
            onFilter={onFilter}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        )}
      </div>
    </div>
  );
}
